import { supabase } from '../lib/supabase';
import { Veiculo, TipoVeiculo, VeiculoMarca, VeiculoModelo, VeiculoCor, VeiculoAno } from '../types/veiculo';
import { pessoaBelongsToGroup } from '../utils/pessoaGroupUtils';
import { veiculoExtraService } from './veiculoExtraService';
import { isPessoaAtiva, getAgregadosMotoristasList } from './pessoaService';

/**
 * Colunas que realmente existem na tabela `veiculos` do Supabase.
 *
 * Campos como `ativo`, `data_desativacao`, `status_frota`, `motivo_manutencao`,
 * `previsao_entrega` e `km_atual` fazem parte do tipo `Veiculo` mas NÃO existem
 * nessa tabela — são guardados em `system_preference` via veiculoExtraService.
 * Se forem enviados no payload, o PostgREST rejeita a requisição inteira
 * (PGRST204 "Could not find the column ... in the schema cache") e NENHUM campo
 * é gravado. Era exatamente isso que impedia a troca de motorista de ser salva.
 */
const COLUNAS_VEICULOS = [
  'id',
  'tipo_veiculo',
  'placa',
  'renavam',
  'crlv',
  'veiculo_ano_id',
  'veiculo_cor_id',
  'veiculo_modelo_id',
  'veiculo_marca_id',
  'motorista_id',
  'agregado_id',
  'valor_repasse_agregado',
] as const;

/** Mantém apenas as colunas que a tabela aceita. */
function somenteColunasReais(veiculo: Partial<Veiculo>, incluirId: boolean): Record<string, any> {
  const payload: Record<string, any> = {};
  for (const coluna of COLUNAS_VEICULOS) {
    if (coluna === 'id' && !incluirId) continue;
    const valor = (veiculo as Record<string, any>)[coluna];
    if (valor !== undefined) payload[coluna] = valor;
  }
  return payload;
}

export const veiculoService = {
  async getAll(): Promise<Veiculo[]> {
    let allVeiculos: Veiculo[] = [];
    let from = 0;
    const step = 500;
    
    while (true) {
      let { data, error } = await supabase
        .from('veiculos')
        .select('*')
        .order('id', { ascending: false })
        .range(from, from + step - 1);

      if (error) {
        const retry = await supabase
          .from('veiculos')
          .select('*')
          .range(from, from + step - 1);
        data = retry.data;
        error = retry.error;
      }

      if (error) {
        console.warn(`[getAll veiculos] Warning fetching page at offset ${from}:`, error.message);
        break;
      }
      
      if (data && data.length > 0) {
        allVeiculos = [...allVeiculos, ...data];
        if (data.length < step) {
          break;
        }
        from += step;
      } else {
        break;
      }
    }

    try {
      const statusMap = await veiculoExtraService.getStatusFrotaMap();
      allVeiculos = allVeiculos.map(v => {
        const statusItem = statusMap[String(v.id)];
        if (statusItem) {
          const isDesativado = statusItem.status_frota === 'desativado' || v.ativo === false;
          return {
            ...v,
            ativo: !isDesativado,
            status_frota: (statusItem.status_frota as any) || (isDesativado ? 'desativado' : (v.status_frota || 'disponivel')),
            motivo_manutencao: statusItem.status_frota === 'em_manutencao' ? (statusItem.motivo_manutencao || v.motivo_manutencao) : null,
            previsao_entrega: statusItem.status_frota === 'em_manutencao' ? (statusItem.previsao_entrega || v.previsao_entrega) : null,
          };
        }
        if (v.ativo === false) {
          return {
            ...v,
            status_frota: 'desativado',
          };
        }
        return v;
      });
    } catch (e) {
      console.warn('Erro ao mesclar statusMap nos veículos:', e);
    }

    return allVeiculos;
  },

  async getById(id: string): Promise<Veiculo | null> {
    const { data, error } = await supabase
      .from('veiculos')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async create(veiculo: Veiculo): Promise<Veiculo> {
    const payload = somenteColunasReais(veiculo, true);

    const { data, error } = await supabase
      .from('veiculos')
      .insert([payload])
      .select()
      .single();

    if (error) throw error;

    // Status da frota / desativação vivem fora da tabela `veiculos`.
    if (veiculo.ativo === false || veiculo.status_frota) {
      const isDesativado = veiculo.ativo === false || veiculo.status_frota === 'desativado';
      try {
        await veiculoExtraService.updateStatusManutencao(
          data.id,
          isDesativado ? 'desativado' : ((veiculo.status_frota as any) || 'disponivel')
        );
      } catch (e) {
        console.warn('Veículo criado, mas houve erro ao salvar o status da frota:', e);
      }
    }

    return { ...data, ativo: veiculo.ativo !== false, status_frota: veiculo.status_frota ?? null };
  },

  async update(id: string, veiculo: Partial<Veiculo>): Promise<Veiculo> {
    // 1. Campos "virtuais" (não existem na tabela) são persistidos à parte.
    if (veiculo.ativo !== undefined || veiculo.status_frota !== undefined) {
      const isDesativado = veiculo.ativo === false || veiculo.status_frota === 'desativado';
      try {
        await veiculoExtraService.updateStatusManutencao(
          id,
          isDesativado ? 'desativado' : ((veiculo.status_frota as any) || 'disponivel')
        );
      } catch (e) {
        console.warn('Erro ao atualizar status extra do veículo:', e);
      }
    }

    // 2. Campos reais da tabela. O `id` nunca é alterado num update.
    const payload = somenteColunasReais(veiculo, false);

    if (Object.keys(payload).length === 0) {
      return { id, ...veiculo } as Veiculo;
    }

    const { data, error } = await supabase
      .from('veiculos')
      .update(payload)
      .eq('id', id)
      .select()
      .maybeSingle();

    // Falhar em silêncio aqui fazia a tela fechar como se tivesse salvo.
    if (error) throw error;
    if (!data) {
      throw new Error(
        `Nenhum veículo foi alterado (id "${id}"). O registro pode ter sido removido ou a permissão de gravação foi negada.`
      );
    }

    return {
      ...data,
      ...(veiculo.ativo !== undefined ? { ativo: veiculo.ativo } : {}),
      ...(veiculo.status_frota !== undefined ? { status_frota: veiculo.status_frota } : {}),
    } as Veiculo;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('veiculos')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getTiposVeiculo(): Promise<TipoVeiculo[]> {
    const { data, error } = await supabase.from('tipo_veiculo').select('*');
    if (error) return [];
    return data || [];
  },

  async getMarcas(): Promise<VeiculoMarca[]> {
    const { data, error } = await supabase.from('veiculo_marca').select('*');
    if (error) return [];
    return data || [];
  },

  async getModelos(): Promise<VeiculoModelo[]> {
    const { data, error } = await supabase.from('veiculo_modelo').select('*');
    if (error) return [];
    return data || [];
  },

  async getCores(): Promise<VeiculoCor[]> {
    const { data, error } = await supabase.from('veiculo_cor').select('*');
    if (error) return [];
    return data || [];
  },

  async getAnos(): Promise<VeiculoAno[]> {
    const { data, error } = await supabase.from('veiculo_ano').select('*');
    if (error) return [];
    return data || [];
  },

  async getAllPessoas() {
    return await supabase.from('pessoa').select('id, nome');
  },

  async getMotoristas(): Promise<{ id: number; nome: string }[]> {
    try {
      // Look for group names containing 'motorista' or 'condutor' with flexible wildcard
      const { data: grupos } = await supabase
        .from('grupo_pessoa')
        .select('id')
        .or('nome.ilike.%motorista%,nome.ilike.%condutor%');
      
      if (grupos && grupos.length > 0) {
        const grupoIds = grupos.map(g => g.id);
        const { data: pessoaIds } = await supabase
          .from('pessoa_grupo')
          .select('pessoa_id')
          .in('grupo_pessoa_id', grupoIds);
        
        if (pessoaIds && pessoaIds.length > 0) {
          const ids = pessoaIds.map(p => p.pessoa_id);
          const { data: pessoas } = await supabase
            .from('pessoa')
            .select('*')
            .in('id', ids)
            .order('nome');
          if (pessoas && pessoas.length > 0) {
            return pessoas.filter(p => isPessoaAtiva(p)).map(p => ({ id: p.id, nome: p.nome }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching filtered motoristas:', err);
    }

    // Fallback: return active people filtered by motoristas group if no specific group links exist
    const { data: allPessoas } = await supabase
      .from('pessoa')
      .select('*')
      .order('nome');
    
    return (allPessoas || [])
      .filter(p => isPessoaAtiva(p) && pessoaBelongsToGroup(p, 'motoristas'))
      .map(p => ({ id: p.id, nome: p.nome }));
  },

  async getAgregados(): Promise<{ id: number; nome: string }[]> {
    return getAgregadosMotoristasList();
  }
};
